import { zodResolver } from "@hookform/resolvers/zod";
import { get } from "lodash";
import { FC, useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router";
import { z } from "zod";

import { FileUploadInput } from "@/components/sku/file-upload-input";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { MusicTabData } from "@/data/types";
import { useCMSStore } from "@/hooks/use-cms-store";
import { FileType } from "@/lib/uploads";

import { useCMSTabContext } from "../cms-tab-context";
import MediaItem from "../media-item";
import { useAudioTabContext } from "./audio-tab-context";

type AudioTrackProps = {
  track: MusicTabData;
  trackIndex: number;
};

const audioTrackFormSchema = z.object({
  track_url: z.url(),
  title: z.string().min(1),
  thumbnail_url: z.union([z.url(), z.literal("")]),
  artist: z.string(),
  isrc: z
    .string()
    .regex(/^[A-Z0-9]+$/, "Only A–Z (uppercase) and 0–9 allowed")
    .regex(
      /^[A-Z]{2}[A-Z0-9]{3}[0-9]{2}[0-9]{5}$/,
      "Invalid ISRC format. Example: USRC17607839.",
    )
    .optional(),
  is_downloadable: z.boolean(),
});

const AudioTrack: FC<AudioTrackProps> = ({ track, trackIndex }) => {
  const { tabIndex } = useCMSTabContext();

  const form = useForm<z.infer<typeof audioTrackFormSchema>>({
    resolver: zodResolver(audioTrackFormSchema),
    mode: "onChange",
    defaultValues: {
      track_url: track.options.track_url,
      title: track.options.title,
      thumbnail_url: track.options.thumbnail_url,
      artist: track.options.artist,
      isrc: track.options.isrc,
      is_downloadable: get(track.options, "is_downloadable", false),
    },
  });
  const { canMoveDown, canMoveUp } = useAudioTabContext();
  const onDelete = useCMSStore.use.onDeletePlaylistTrack();
  const onMove = useCMSStore.use.onMovePlaylistTrack();
  const onUpdate = useCMSStore.use.onUpdatePlaylistTrack();
  const setValidationErrors = useCMSStore.use.setValidationErrors();

  const {
    formState: { isValid },
  } = form;

  useEffect(() => {
    setValidationErrors(!isValid);
  }, [isValid, setValidationErrors]);

  useEffect(() => {
    const unsubscribe = form.subscribe({
      formState: { values: true },
      callback: ({ values }) => {
        onUpdate(tabIndex, trackIndex, values);
      },
    });

    return unsubscribe;
  }, [form, onUpdate, tabIndex, trackIndex]);

  useEffect(() => {
    const newValue = track.options.is_downloadable ?? false;
    if (form.getValues("is_downloadable") !== newValue) {
      form.setValue("is_downloadable", newValue, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [track.options.is_downloadable, form]);

  const handleMove = useCallback(
    (oldIndex: number, newIndex: number) => {
      onMove(tabIndex, oldIndex, newIndex);
    },
    [tabIndex, onMove],
  );

  const handleDelete = useCallback(() => {
    onDelete(tabIndex, trackIndex);
  }, [onDelete, tabIndex, trackIndex]);

  const trackCanMoveUp = useMemo(
    () => canMoveUp(trackIndex),
    [canMoveUp, trackIndex],
  );

  const trackCanMoveDown = useMemo(
    () => canMoveDown(trackIndex),
    [canMoveDown, trackIndex],
  );

  return (
    <MediaItem
      index={trackIndex}
      title={track.options.title}
      description={track.options.artist}
      onDelete={handleDelete}
      mediaType={FileType.AUDIO}
      canMoveUp={trackCanMoveUp}
      canMoveDown={trackCanMoveDown}
      onMove={handleMove}
      isDownloadable={track.options.is_downloadable}
    >
      <Form {...form}>
        <div className="bg-gray-50 flex flex-col gap-3 items-end p-4 xl:pl-32">
          <FormField
            name="track_url"
            control={form.control}
            render={({ field }) => (
              <FormItem className="flex w-full gap-4 items-center">
                <FormLabel className="min-w-20 justify-end">
                  Audio file*
                </FormLabel>
                <FormControl>
                  <FileUploadInput
                    {...field}
                    fileType={FileType.AUDIO}
                    className="bg-white flex-1"
                    removeDisabled
                    previewDisabled
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            name="thumbnail_url"
            control={form.control}
            render={({ field }) => (
              <FormItem className="flex w-full gap-4 items-center">
                <FormLabel className="min-w-20 justify-end">
                  Thumbnail
                </FormLabel>
                <FormControl>
                  <FileUploadInput
                    {...field}
                    fileType={FileType.IMAGE}
                    className="bg-white flex-1"
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            name="title"
            control={form.control}
            render={({ field }) => (
              <FormItem className="flex w-full gap-4 items-center">
                <FormLabel className="min-w-20 justify-end">
                  Track Name*
                </FormLabel>
                <div className="flex-1 space-y-1">
                  <FormControl>
                    <Input
                      {...field}
                      className="bg-white flex-1"
                      placeholder="Track Name"
                    />
                  </FormControl>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          <FormField
            name="artist"
            control={form.control}
            render={({ field }) => (
              <FormItem className="flex w-full gap-4 items-center">
                <FormLabel className="min-w-20 justify-end">
                  Artist Name
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="bg-white flex-1"
                    placeholder="Artist Name"
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            name="isrc"
            control={form.control}
            render={({ field }) => (
              <FormItem className="flex w-full gap-4 items-start">
                <FormLabel className="min-w-20 pt-3 justify-end">
                  ISRC
                </FormLabel>
                <div className="flex-1 space-y-1.5">
                  <FormControl>
                    <Input
                      {...field}
                      className="bg-white flex-1"
                      placeholder="ISRC (Optional)"
                    />
                  </FormControl>
                  <FormDescription>
                    An ISRC (International Standard Recording Code) is a unique
                    ID for a track.{" "}
                    <Link to="" className="underline">
                      Learn More
                    </Link>
                  </FormDescription>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          <FormField
            name="is_downloadable"
            control={form.control}
            render={({ field }) => (
              <FormItem className="flex w-full gap-4 items-center py-4">
                <FormLabel className="min-w-20 justify-end">
                  Downloadable
                </FormLabel>
                <FormControl>
                  <div className="flex items-center gap-4">
                    <Switch
                      className="scale-150 mr-2"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                    <p className="text-sm text-muted-foreground">
                      Allow consumers to download this audio file
                    </p>
                  </div>
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </Form>
    </MediaItem>
  );
};

type AudioTracksProps = {
  tracks: MusicTabData[];
};

export const AudioTracks: FC<AudioTracksProps> = ({ tracks }) => (
  <div className="space-y-4">
    {tracks.map((track, index) => (
      <AudioTrack
        key={track.options.track_url}
        track={track}
        trackIndex={index}
      />
    ))}
  </div>
);
